import React, { useContext } from 'react'

import { Chip } from '@clientbase/clientbase-library'
import { Box, Typography } from '@mui/material'
import useTags from 'hooks/useTags'

import { DetailCompanyRow } from 'components/DetailCompany'
import ChipTag from 'components/Tag'

import { formatMoney } from 'utils'
import { formatDate } from 'utils/FormatDate'
import { convertTagsStringToArray } from 'utils/tags'

import { theme } from 'styles/theme'

import { ModalInstallmentDetailContext } from '../../context'
import * as S from './InstallmentDetails.styles'

const InstallmentDetails: React.FC = () => {
  const { installment } = useContext(ModalInstallmentDetailContext)
  const { tags: availableTags } = useTags()
  const tags = convertTagsStringToArray(
    (installment as any)?.tags,
    availableTags
  )

  const getFirstDueDate = () => {
    if (!installment.billings || installment.billings.length === 0) return ''

    const sortedBillings = installment.billings.sort((a, b) => {
      const dateA = new Date(a.due_date)
      const dateB = new Date(b.due_date)
      return dateA.getTime() - dateB.getTime()
    })

    return sortedBillings[0]?.due_date || ''
  }

  const getExpirationDays = () => {
    if (!installment.billings || installment.billings.length === 0) return '00'

    const firstBilling = installment.billings.find(
      (b) => b.installment_cycle === 1
    )
    if (!firstBilling) return '00'

    const dueDate = new Date(firstBilling.due_date)
    const expirationDate = new Date(firstBilling.expiration_date)
    const diffTime = expirationDate.getTime() - dueDate.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    return diffDays.toString().padStart(2, '0')
  }

  const hasDiscount = () => {
    return false // Por enquanto não temos informação de desconto no installment
  }

  const hasInterest = () => {
    return false // Por enquanto não temos informação de juros no installment
  }

  const hasCardFeePassthrough = () => {
    return installment.payment_type.includes('credit_card')
  }

  const getCreditCardInstallmentInfo = () => {
    if (
      !installment.payment_type.includes('credit_card') ||
      !installment.paymentTypeDetails
    ) {
      return null
    }

    const cc = installment.paymentTypeDetails.credit_card
    if (!cc?.enabled) {
      return null
    }

    return {
      maxInstallments: Number(cc.max_installments || 1),
      maxInstallmentsWithoutFee: Number(cc.max_installments_without_fee || 0)
    }
  }

  const getRecurrenceCycleInfo = () => {
    if (!installment.billings || installment.billings.length === 0) return null

    const billingWithRecurrence = installment.billings.find(
      (billing) => billing.recurrence_cycle && billing.recurrence
    )

    if (!billingWithRecurrence) return null

    return {
      currentCycle: billingWithRecurrence.recurrence_cycle || 0,
      totalCycles: billingWithRecurrence.recurrence?.total_cycles || 0
    }
  }

  const recurrenceCycleInfo = getRecurrenceCycleInfo()

  const getPaymentMethodLabel = (paymentType: string) => {
    const paymentLabels: { [key: string]: string } = {
      credit_card: 'CARTÃO DE CRÉDITO',
      credit_card_recurring: 'CARTÃO RECORRENTE',
      credit_card_installments: 'CARTÃO PARCELADO',
      debit_card: 'CARTÃO DE DÉBITO',
      boleto: 'BOLETO',
      pix: 'PIX',
      boleto_pix: 'BOLETO/PIX',
      cash: 'DINHEIRO',
      transfer: 'TRANSFERÊNCIA (TED/DOC)',
      other: 'OUTROS',
      direct_payment: 'RECEBIMENTO MANUAL'
    }
    return paymentLabels[paymentType] || paymentType.toUpperCase()
  }

  const getPaymentTypes = () => {
    if (!installment.payment_type) return []
    return installment.payment_type.split(';').filter(Boolean)
  }

  return (
    <S.Wrapper>
      <Box>
        <S.ChargeSession>
          {/* SC-10999: de qual empresa é o parcelamento, na visão multi-empresas */}
          <DetailCompanyRow
            recordUuid={installment?.uuid}
            margin="10px 0px 10px 0px"
          />
          <Box
            display="flex"
            flexWrap="wrap"
            gap={1.25}
            alignItems="end"
            margin="10px 0px 10px 0px"
          >
            <Typography variant="smLight" color={theme.palette.neutral[400]}>
              Valor total
            </Typography>
            <S.Separator sx={{ marginTop: '4px' }} />
            <Typography variant="smBold" display="flex" alignItems="center">
              {formatMoney(installment?.amount?.toString() || '0')}
            </Typography>
          </Box>
          <Box
            display="flex"
            gap={1.25}
            alignItems="end"
            margin="10px 0px 10px 0px"
          >
            <Typography variant="smLight" color={theme.palette.neutral[400]}>
              Dia de vencimento
            </Typography>
            <S.Separator sx={{ marginTop: '4px' }} />
            <Typography variant="smBold" display="flex" alignItems="center">
              {formatDate(getFirstDueDate())?.split('/')[0]}
            </Typography>
          </Box>
          <Box
            display="flex"
            gap={1.25}
            alignItems="end"
            margin="10px 0px 10px 0px"
          >
            <Typography variant="smLight" color={theme.palette.neutral[400]}>
              Dias para expirar
            </Typography>
            <S.Separator sx={{ marginTop: '4px' }} />
            <Typography variant="smBold" display="flex" alignItems="center">
              {getExpirationDays()}
            </Typography>
          </Box>
          <Box
            display="flex"
            gap={1.25}
            alignItems="end"
            margin="10px 0px 10px 0px"
          >
            <Typography variant="smLight" color={theme.palette.neutral[400]}>
              Desconto
            </Typography>
            <S.Separator sx={{ marginTop: '4px' }} />
            <Typography variant="smBold" display="flex" alignItems="center">
              {hasDiscount() ? 'Sim' : 'Não'}
            </Typography>
          </Box>
          <Box
            display="flex"
            gap={1.25}
            alignItems="end"
            margin="10px 0px 10px 0px"
          >
            <Typography variant="smLight" color={theme.palette.neutral[400]}>
              Juros
            </Typography>
            <S.Separator sx={{ marginTop: '4px' }} />
            <Typography variant="smBold" display="flex" alignItems="center">
              {hasInterest() ? 'Sim' : 'Não'}
            </Typography>
          </Box>
          {recurrenceCycleInfo && (
            <Box
              display="flex"
              gap={1.25}
              alignItems="end"
              margin="10px 0px 10px 0px"
            >
              <Typography variant="smLight" color={theme.palette.neutral[400]}>
                Ciclo
              </Typography>
              <S.Separator sx={{ marginTop: '4px' }} />
              <Typography variant="smBold" display="flex" alignItems="center">
                {recurrenceCycleInfo.currentCycle}/
                {recurrenceCycleInfo.totalCycles === 0
                  ? 'Indeterminado'
                  : recurrenceCycleInfo.totalCycles}
              </Typography>
            </Box>
          )}
          <Box
            display="flex"
            gap={1.25}
            alignItems="end"
            margin="10px 0px 10px 0px"
          >
            <Typography variant="smLight" color={theme.palette.neutral[400]}>
              Parcelas
            </Typography>
            <S.Separator sx={{ marginTop: '4px' }} />
            <Typography variant="smBold" display="flex" alignItems="center">
              {installment?.total_cycles}
            </Typography>
          </Box>
          {/* Repasse de taxas no cartão - só mostra se tiver pagamento por cartão */}
          {hasCardFeePassthrough() && (
            <Box
              display="flex"
              gap={1.25}
              alignItems="end"
              margin="10px 0px 10px 0px"
            >
              <Typography variant="smLight" color={theme.palette.neutral[400]}>
                Repasse de taxas no cartão
              </Typography>
              <S.Separator sx={{ marginTop: '4px' }} />
              <Typography variant="smBold" display="flex" alignItems="center">
                {(() => {
                  const cardInfo = getCreditCardInstallmentInfo()
                  if (cardInfo) {
                    return `${cardInfo.maxInstallments}x (${cardInfo.maxInstallmentsWithoutFee}x s/ juros)`
                  }
                  return 'Sim'
                })()}
              </Typography>
            </Box>
          )}

          {/* Meio de pagamento utilizado */}
          <Typography variant="smLight" color={theme.palette.neutral[400]}>
            Meio de pagamento utilizado
          </Typography>
          <Box
            display="flex"
            flexWrap="wrap"
            gap="8px"
            margin="10px 0px 10px 0px"
          >
            {getPaymentTypes().map(
              (item: string) =>
                item && (
                  <Chip
                    variant="payment"
                    sx={(theme: any) => ({
                      background: theme.palette.neutral[400]
                    })}
                    key={String(item)}
                    label={getPaymentMethodLabel(item)}
                  />
                )
            )}
          </Box>

          {/* Descrição */}
          {installment?.description && (
            <Box
              margin="10px 0px 10px 0px"
              display="flex"
              flexDirection="column"
              gap={1}
            >
              <Typography variant="smLight" color={theme.palette.neutral[400]}>
                Descrição
              </Typography>
              <Typography
                color={(theme) => theme.palette.neutral[200]}
                sx={{ whiteSpace: 'pre-line' }}
              >
                {installment?.description}
              </Typography>
            </Box>
          )}
          {tags.length > 0 && (
            <Box
              margin="10px 0px 10px 0px"
              display="flex"
              flexDirection="column"
              gap={1}
            >
              <Typography variant="smLight" color={theme.palette.neutral[400]}>
                Tags
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {tags.map((tag: any, index: number) => (
                  <ChipTag
                    key={tag.id || index}
                    tag={{ id: tag.id, name: tag.name, color: tag.color }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </S.ChargeSession>
      </Box>
    </S.Wrapper>
  )
}

export default InstallmentDetails
